import React, {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTranslate } from '@/translations';
import {
  CreateDamageLineBody,
  Damage,
  DamageImageFile,
  DamageLineFormType,
  Inspection,
  Select as SelectModel,
  UpdateDamageLineBody,
} from '@/types';
import { estimateVehicleGrade } from './estimateFinalGrade';
import { canEditWorkCentre } from '../canEditWorkCentre';
import {
  hasClassificationChange,
  optionLabel,
  sectorIndicesToMask,
  type StagedDamageEdit,
} from '../DamageDataGrid/stagedDamage';
import {
  useCreateInspectionsVehiclesDamagesLines,
  useInspectionsVehicleDamageCodes,
  useInspectionsVehicleDamageConditionCodes,
  useInspectionsVehicleDamageParts,
  useInspectionsVehicleRepairActionCodes,
  useInspectionsVehicleSeverityCodes,
} from '@/api/Inspections';
import { useAppCustomers } from '@/hooks';
import {
  acceptImageFiles,
  CarDamageAreaCode,
  MAX_FILE_SIZE,
} from '@/utils/constants';
import {
  useMechanicalInspection,
  useMechanicalInspectionAvailable,
} from '@/api/MechanicalInspection';

import {
  App,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  UploadFile,
} from 'antd';
import {
  BarChartOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  InfoCircleOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { DragUpload } from '@/ui/components';
import { confirmDiscardUnsaved } from '../confirmDiscardUnsaved';
import { isDropdownOpen } from '../inlineCellNavigation';
import {
  isSaveNotesShortcut,
  saveNotesShortcutHint,
} from '../preTimingShortcuts';

const preventEnterSaveWhileDropdownOpen = (
  event: React.KeyboardEvent<HTMLFormElement>,
) => {
  if (event.key !== 'Enter' || !isDropdownOpen(event.target)) return;
  event.preventDefault();
};
import {
  Card,
  CardTitle,
  CodeTag,
  EditAside,
  EditColumns,
  EditTitleHint,
  EditTitleRow,
  EditMain,
  FieldGrid,
  FieldPair,
  Footer,
  FooterActions,
  GradeArrow,
  GradeBox,
  GradeCard,
  GradeLabel,
  GradeMetric,
  GradeSummary,
  GradeTitle,
  GradeValues,
  Hint,
  Layout,
  MiniLabel,
  MiniValue,
  StagingModeBadge,
  StagingModeDot,
  ReadOnlyItem,
  ReadOnlyLabel,
  ReadOnlyValue,
  TagList,
  TopRow,
} from './AuthorisationDamageLayout.styled';

const areaCodeOptions: SelectModel<CarDamageAreaCode>[] = [
  {
    label: 'Front',
    value: CarDamageAreaCode.Front,
  },
  {
    label: 'Nearside',
    value: CarDamageAreaCode.RightSide,
  },
  {
    label: 'Rear',
    value: CarDamageAreaCode.Rear,
  },
  {
    label: 'Offside',
    value: CarDamageAreaCode.LeftSide,
  },
  {
    label: 'Top',
    value: CarDamageAreaCode.Top,
  },
  {
    label: 'Outside',
    value: CarDamageAreaCode.Outside,
  },
  {
    label: 'Underbody',
    value: CarDamageAreaCode.Underbody,
  },
  {
    label: 'Interior',
    value: CarDamageAreaCode.Interior,
  },
];

const sectorIndicesOptions = Array(9)
  .fill(null)
  .map((_: null, idx: number) => idx + 1);

const EMPTY_DAMAGE_LINE_FORM: DamageLineFormType = {
  areaCode: '' as CarDamageAreaCode,
  partCode: '',
  damageCauseCode: '',
  sectorIndices: [],
  damageConditionCode: '',
  severityId: '' as unknown as number,
  repairMethodCode: '',
  internalGradingId: '' as unknown as number,
  workCenterCode: '',
  responsibilityCode: '',
  responsibilityShortcutCode: '',
  dealerCode: '',
  note: '',
  mechanicalFailure: '',
  damageImageFiles: [],
};

const toFormValues = (damageLine?: Damage): DamageLineFormType => {
  if (!damageLine) return EMPTY_DAMAGE_LINE_FORM;

  return {
    areaCode:
      (damageLine.areaCode as CarDamageAreaCode) ||
      EMPTY_DAMAGE_LINE_FORM.areaCode,
    partCode: damageLine.partCode || EMPTY_DAMAGE_LINE_FORM.partCode,
    damageCauseCode:
      damageLine.damageCauseCode || EMPTY_DAMAGE_LINE_FORM.damageCauseCode,
    sectorIndices:
      damageLine.sectorIndices || EMPTY_DAMAGE_LINE_FORM.sectorIndices,
    damageConditionCode:
      damageLine.damageConditionCode ||
      EMPTY_DAMAGE_LINE_FORM.damageConditionCode,
    severityId: damageLine.severityId || EMPTY_DAMAGE_LINE_FORM.severityId,
    repairMethodCode:
      damageLine.repairActionCode || EMPTY_DAMAGE_LINE_FORM.repairMethodCode,
    internalGradingId:
      damageLine.internalGradingId || EMPTY_DAMAGE_LINE_FORM.internalGradingId,
    workCenterCode:
      damageLine.workCenterCode || EMPTY_DAMAGE_LINE_FORM.workCenterCode,
    responsibilityCode:
      damageLine.responsibilityCode ||
      EMPTY_DAMAGE_LINE_FORM.responsibilityCode,
    responsibilityShortcutCode:
      damageLine.responsibilityShortcutCode ||
      EMPTY_DAMAGE_LINE_FORM.responsibilityShortcutCode,
    dealerCode: damageLine.dealerCode || EMPTY_DAMAGE_LINE_FORM.dealerCode,
    note: damageLine.note || EMPTY_DAMAGE_LINE_FORM.note,
    mechanicalFailure:
      damageLine.mechanicalFailure || EMPTY_DAMAGE_LINE_FORM.mechanicalFailure,
    damageImageFiles: [],
  };
};

type FormProps = {
  formId: string;
  inspection: Inspection;
  damageLine?: Damage;
  originalDamageLine?: Damage;
  onSubmit: (
    data: DamageLineFormType,
    staged?: Pick<
      StagedDamageEdit,
      'display' | 'salesGradeValue' | 'valueForGrading'
    >,
  ) => Promise<void>;
  presentation: 'default' | 'authorisation';
  loading?: boolean;
  undoRef: MutableRefObject<() => void>;
  onDirtyChange?: (dirty: boolean) => void;
};

const DamageLineForm: FC<FormProps> = ({
  formId,
  inspection,
  damageLine,
  originalDamageLine,
  onSubmit,
  presentation,
  loading,
  undoRef,
  onDirtyChange,
}) => {
  const inspectionId = inspection.id;
  const siteId = inspection.locationId;
  const barCode = inspection.vin;
  const showDamageImageFiles = presentation === 'default';
  const defaultValue = useMemo(() => toFormValues(damageLine), [damageLine]);
  const originalValue = useMemo(
    () => toFormValues(originalDamageLine ?? damageLine),
    [damageLine, originalDamageLine],
  );
  const originalValueRef = useRef(originalValue);
  originalValueRef.current = originalValue;
  const { t } = useTranslate();
  const [form] = Form.useForm<DamageLineFormType>();
  const areaCode = Form.useWatch('areaCode', form);
  const partCode = Form.useWatch('partCode', form);
  const damageConditionCode = Form.useWatch('damageConditionCode', form);
  const severityId = Form.useWatch('severityId', form);
  const responsibilityCode = Form.useWatch('responsibilityCode', form);

  const [files, setFiles] = useState<DamageImageFile[]>([]);

  undoRef.current = () => {
    form.setFieldsValue(originalValueRef.current);
    setFiles([]);
    onDirtyChange?.(false);
  };

  const { data: vehicleDamageParts, isFetching: isDamagePartsLoading } =
    useInspectionsVehicleDamageParts(inspectionId);
  const partCodeOptionsMap = useMemo(
    () =>
      vehicleDamageParts?.selectItems ||
      ({} as Record<CarDamageAreaCode, SelectModel[]>),
    [vehicleDamageParts],
  );

  const { data: allDamageCodes, isFetching: isDamageCodesLoading } =
    useInspectionsVehicleDamageCodes(inspectionId, siteId);

  const {
    data: damageConditionCodes,
    isFetching: isDamageConditionCodesLoading,
  } = useInspectionsVehicleDamageConditionCodes(inspectionId, partCode);

  const {
    data: damageSeverityCodes,
    isFetching: isDamageSeverityCodesLoading,
  } = useInspectionsVehicleSeverityCodes(
    inspectionId,
    partCode,
    damageConditionCode,
  );

  const {
    data: damageRepairMethods,
    isFetching: isDamageRepairMethodsLoading,
  } = useInspectionsVehicleRepairActionCodes(
    inspectionId,
    partCode,
    damageConditionCode,
    severityId,
  );

  const { data: mechanicalInspectionAvailable } =
    useMechanicalInspectionAvailable(inspectionId, barCode);

  const {
    data: mechanicalInspectionData,
    isFetching: isMechanicalInspectionDataLoading,
  } = useMechanicalInspection(inspectionId, barCode, {
    enabled: !!mechanicalInspectionAvailable?.inspectionAvailable,
  });

  const responsibilityShortcutsOptions = useMemo(
    () =>
      allDamageCodes?.responsibilities?.getResponsibilityShortcutsOptions(
        responsibilityCode,
      ),
    [allDamageCodes?.responsibilities, responsibilityCode],
  );
  const availableDealersOptions = useMemo(
    () =>
      allDamageCodes?.responsibilities?.getDealersOptions(responsibilityCode),
    [allDamageCodes?.responsibilities, responsibilityCode],
  );

  const isDealerCodeRequired = !!availableDealersOptions?.length;

  useEffect(() => {
    setFiles([]);
    form.resetFields();
    form.setFieldsValue(defaultValue);
  }, [defaultValue, form]);

  const handleValueChange = (files: UploadFile[]) => {
    if (files.length === 0) {
      setFiles([]);
      return;
    }
    const readers: Promise<unknown>[] = [];

    // convert to base64 string
    const readFileAsDataUrl = (file: File) =>
      new Promise((resolve, reject) => {
        const fr = new FileReader();

        fr.onload = () => {
          resolve(fr.result);
        };

        fr.onerror = () => {
          reject(fr);
        };

        fr.readAsDataURL(file);
      });

    files.forEach((file) => {
      const item = file.originFileObj as File;
      readers.push(readFileAsDataUrl(item));
    });

    Promise.all(readers).then((base64Array) => {
      setFiles(
        base64Array.map((base64, idx) => ({
          imageFileName: files[idx].name,
          imageFileBase64: base64 as string,
        })),
      );
    });
  };

  const selectedSeverity = damageSeverityCodes?.find(
    (item) => item.severityId === Number(severityId),
  );

  const handleSubmit = useCallback(
    async (data: DamageLineFormType) => {
      data.damageImageFiles = files;
      const indices = (data.sectorIndices || []).filter(Boolean);
      const conditionOptions = (damageConditionCodes || []).map((item) => ({
        label: item.description,
        value: item.code,
      }));
      const repairOptions = (damageRepairMethods || []).map((item) => ({
        label: item.description,
        value: item.code,
      }));
      const display: Partial<Damage> = {
        areaCode: data.areaCode,
        damageAreaDesc:
          optionLabel(areaCodeOptions, data.areaCode) ??
          damageLine?.damageAreaDesc,
        partCode: data.partCode,
        damagePartDesc:
          optionLabel(partCodeOptionsMap[data.areaCode], data.partCode) ??
          damageLine?.damagePartDesc,
        damageCauseCode: data.damageCauseCode,
        damageCauseDesc:
          optionLabel(allDamageCodes?.damageCauses, data.damageCauseCode) ??
          damageLine?.damageCauseDesc,
        damageConditionCode: data.damageConditionCode,
        damageTypeDesc:
          optionLabel(conditionOptions, data.damageConditionCode) ??
          damageLine?.damageTypeDesc,
        severityId: data.severityId,
        severityDesc: selectedSeverity?.description ?? damageLine?.severityDesc,
        repairActionCode: data.repairMethodCode,
        repairActionDesc:
          optionLabel(repairOptions, data.repairMethodCode) ??
          damageLine?.repairActionDesc,
        internalGradingId: data.internalGradingId,
        internalGradingDesc:
          optionLabel(
            allDamageCodes?.internalGradings,
            data.internalGradingId,
          ) ?? damageLine?.internalGradingDesc,
        workCenterCode: data.workCenterCode,
        workCenterDesc:
          optionLabel(allDamageCodes?.workCenters, data.workCenterCode) ??
          damageLine?.workCenterDesc,
        responsibilityCode: data.responsibilityCode,
        responsibilityDesc:
          optionLabel(
            allDamageCodes?.responsibilities?.options,
            data.responsibilityCode,
          ) ?? damageLine?.responsibilityDesc,
        responsibilityShortcutCode: data.responsibilityShortcutCode,
        dealerCode: data.dealerCode,
        note: data.note,
        sectorIndices: indices,
        sector: sectorIndicesToMask(indices),
        mechanicalFailure: data.mechanicalFailure,
      };
      await onSubmit(data, {
        display,
        salesGradeValue: selectedSeverity?.salesGradeValue,
        valueForGrading: selectedSeverity?.valueForGrading ?? null,
      });
    },
    [
      allDamageCodes?.damageCauses,
      allDamageCodes?.internalGradings,
      allDamageCodes?.responsibilities?.options,
      allDamageCodes?.workCenters,
      damageConditionCodes,
      damageLine?.damageAreaDesc,
      damageLine?.damageCauseDesc,
      damageLine?.damagePartDesc,
      damageLine?.damageTypeDesc,
      damageLine?.internalGradingDesc,
      damageLine?.repairActionDesc,
      damageLine?.responsibilityDesc,
      damageLine?.severityDesc,
      damageLine?.workCenterDesc,
      damageRepairMethods,
      files,
      onSubmit,
      partCodeOptionsMap,
      selectedSeverity?.description,
      selectedSeverity?.salesGradeValue,
      selectedSeverity?.valueForGrading,
    ],
  );

  const DEFAULT_OPTION = 'Choose option';
  const emptyValue = t('notAvailable');
  const frCodeList = (damageLine?.actions ?? [])
    .map((action) => action.flatRateCode)
    .filter((code): code is string => Boolean(code));
  const partNumberList = (damageLine?.actions ?? [])
    .flatMap((action) => action.parts ?? [])
    .map((part) => part.partNumber)
    .filter((partNumber): partNumber is string => Boolean(partNumber));
  const labour = damageLine?.actions?.find(
    (action) => action.labourCode || action.labourType,
  );
  const labourTime = labour
    ? [labour.quantity ? `${labour.quantity}` : '', labour.labourType]
        .filter(Boolean)
        .join(' ')
    : '';
  const gradeEstimate = estimateVehicleGrade({
    finalGradeScore: inspection.finalGradeScore,
    finalGradePoints: inspection.finalGradePoints,
    currentSalesGradeValue: damageLine?.salesGradeValue,
    nextSalesGradeValue: selectedSeverity?.salesGradeValue,
  });
  const originalGradeValue = gradeEstimate.originalScore;
  const latestGradeValue = gradeEstimate.latestScore;
  const originalPointsValue = gradeEstimate.originalPoints;
  const latestPointsValue = gradeEstimate.latestPoints;
  const describeChange = (
    original: number | undefined,
    latest: number | undefined,
    improvedVerb: string,
    worsenedVerb: string,
    unchangedVerb: string,
  ) => {
    if (original === undefined || latest === undefined) return null;
    const delta = original - latest;
    if (delta === 0) return { tone: 'neutral' as const, verb: unchangedVerb };
    if (delta > 0) {
      return { tone: 'improved' as const, verb: improvedVerb, amount: delta };
    }
    return {
      tone: 'worsened' as const,
      verb: worsenedVerb,
      amount: Math.abs(delta),
    };
  };
  const gradeChange = describeChange(
    originalGradeValue,
    latestGradeValue,
    t('gradeImpactImproves'),
    t('gradeImpactWorsens'),
    t('gradeImpactGradeUnchanged'),
  ) ?? { tone: 'neutral' as const, verb: t('gradeImpactGradeUnchanged') };
  const pointsChange = describeChange(
    originalPointsValue,
    latestPointsValue,
    t('gradeImpactReduce'),
    t('gradeImpactIncrease'),
    t('gradeImpactPointsUnchanged'),
  ) ?? { tone: 'neutral' as const, verb: t('gradeImpactPointsUnchanged') };
  const summaryTone =
    gradeChange?.tone === 'worsened' || pointsChange?.tone === 'worsened'
      ? 'worsened'
      : gradeChange?.tone === 'improved' || pointsChange?.tone === 'improved'
        ? 'improved'
        : 'neutral';
  const displayMetric = (value?: number) =>
    value === undefined ? emptyValue : String(value);
  const canChangePart =
    inspection?.permissions?.canChangePartComponent ?? false;
  const canChangeDamageType =
    inspection?.permissions?.canChangeDamageType ?? false;
  const canEditDamage = inspection?.permissions?.canEditDamage ?? false;
  const canChangeSeverity = inspection?.permissions?.canChangeSeverity ?? false;
  const canChangeRepairMethod =
    inspection?.permissions?.canChangeRepairMethod ?? false;
  const classificationChanged = hasClassificationChange(originalValue, {
    partCode: partCode != null ? partCode : originalValue.partCode,
    damageConditionCode:
      damageConditionCode != null
        ? damageConditionCode
        : originalValue.damageConditionCode,
  });
  const currentSeverityId =
    severityId != null ? severityId : originalValue.severityId;
  const severityChanged =
    String(currentSeverityId ?? '') !== String(originalValue.severityId ?? '');
  const severityEditable = canChangeSeverity || classificationChanged;
  const repairEditable =
    canChangeRepairMethod || classificationChanged || severityChanged;
  const workCentreEditable = canEditWorkCentre({
    canChangeWorkCenter: inspection?.permissions?.canChangeWorkCenter,
    isDeleted: damageLine?.isDeleted,
    mechanicalFailure: damageLine?.mechanicalFailure,
  });

  if (presentation === 'authorisation') {
    return (
      <Spin spinning={loading}>
        <Form
          id={formId}
          form={form}
          onFinish={handleSubmit}
          onValuesChange={() => onDirtyChange?.(true)}
          onKeyDown={preventEnterSaveWhileDropdownOpen}
          layout="vertical"
          size="middle"
        >
          <Layout>
            <TopRow>
              <Card>
                <CardTitle>
                  {t('customerEditableInformation', {
                    customerName: inspection.customerName,
                  })}
                </CardTitle>
                <FieldGrid>
                  <Form.Item
                    label={t('partComp')}
                    name="partCode"
                    rules={[{ required: true }]}
                  >
                    <Select
                      showSearch
                      disabled={!canChangePart}
                      placeholder={DEFAULT_OPTION}
                      options={areaCode ? partCodeOptionsMap[areaCode] : []}
                      loading={isDamagePartsLoading}
                      onChange={() => {
                        form.setFieldValue('damageConditionCode', '');
                        form.setFieldValue('severityId', '');
                        form.setFieldValue('repairMethodCode', '');
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label={t('damageType')}
                    name="damageConditionCode"
                    rules={[{ required: true }]}
                  >
                    <Select
                      showSearch
                      disabled={!canChangeDamageType}
                      placeholder={DEFAULT_OPTION}
                      loading={
                        isDamageCodesLoading || isDamageConditionCodesLoading
                      }
                      options={damageConditionCodes?.map((item) => ({
                        label: item.description,
                        value: item.code,
                      }))}
                      onChange={() => {
                        form.setFieldValue('severityId', '');
                        form.setFieldValue('repairMethodCode', '');
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label={t('severity')}
                    name="severityId"
                    rules={
                      damageSeverityCodes?.length
                        ? [{ required: true }]
                        : undefined
                    }
                  >
                    <Select
                      showSearch
                      disabled={!severityEditable}
                      placeholder={DEFAULT_OPTION}
                      loading={
                        isDamageCodesLoading ||
                        isDamageConditionCodesLoading ||
                        isDamageSeverityCodesLoading
                      }
                      options={damageSeverityCodes?.map((item) => ({
                        label: item.description,
                        value: item.severityId,
                      }))}
                      onChange={() =>
                        form.setFieldValue('repairMethodCode', '')
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    label={t('repairMethod')}
                    name="repairMethodCode"
                    rules={[{ required: true }]}
                  >
                    <Select
                      showSearch
                      disabled={!repairEditable}
                      placeholder={DEFAULT_OPTION}
                      loading={
                        isDamageCodesLoading ||
                        isDamageConditionCodesLoading ||
                        isDamageSeverityCodesLoading ||
                        isDamageRepairMethodsLoading
                      }
                      options={damageRepairMethods?.map((item) => ({
                        label: item.description,
                        value: item.code,
                      }))}
                    />
                  </Form.Item>
                </FieldGrid>
                <Hint>
                  <InfoCircleOutlined />
                  <span>{t('gradeUpdateHint')}</span>
                </Hint>
              </Card>
              <GradeCard>
                <GradeTitle>
                  <BarChartOutlined style={{ color: '#2f6fed' }} />
                  {t('vehicleGradeImpact')}
                </GradeTitle>
                <GradeMetric>
                  <GradeLabel>{t('vehicleGrade')}</GradeLabel>
                  <GradeValues>
                    <GradeBox>
                      <MiniLabel>{t('originalValue')}</MiniLabel>
                      <MiniValue>{displayMetric(originalGradeValue)}</MiniValue>
                    </GradeBox>
                    <GradeArrow>→</GradeArrow>
                    <GradeBox>
                      <MiniLabel>{t('newValue')}</MiniLabel>
                      <MiniValue $tone={gradeChange?.tone ?? 'neutral'}>
                        {displayMetric(latestGradeValue)}
                      </MiniValue>
                    </GradeBox>
                  </GradeValues>
                </GradeMetric>
                <GradeMetric>
                  <GradeLabel>{t('gradePoints')}</GradeLabel>
                  <GradeValues>
                    <GradeBox>
                      <MiniLabel>{t('originalValue')}</MiniLabel>
                      <MiniValue>
                        {displayMetric(originalPointsValue)}
                      </MiniValue>
                    </GradeBox>
                    <GradeArrow>→</GradeArrow>
                    <GradeBox>
                      <MiniLabel>{t('newValue')}</MiniLabel>
                      <MiniValue $tone={pointsChange?.tone ?? 'neutral'}>
                        {displayMetric(latestPointsValue)}
                      </MiniValue>
                    </GradeBox>
                  </GradeValues>
                </GradeMetric>
                <GradeSummary $tone={summaryTone}>
                  {summaryTone === 'worsened' ? (
                    <CloseCircleFilled
                      style={{ color: '#d92d20', marginTop: 2 }}
                    />
                  ) : (
                    <CheckCircleFilled
                      style={{
                        color:
                          summaryTone === 'improved' ? '#12b76a' : '#98a2b3',
                        marginTop: 2,
                      }}
                    />
                  )}
                  <span>
                    Vehicle grade <strong>{gradeChange.verb}</strong>
                    {gradeChange.amount ? ` by ${gradeChange.amount}` : ''} and
                    grade points <strong>{pointsChange.verb}</strong>
                    {pointsChange.amount ? ` by ${pointsChange.amount}` : ''}.
                  </span>
                </GradeSummary>
              </GradeCard>
            </TopRow>
            <Card>
              <EditColumns>
                <EditMain>
                  <CardTitle>{t('gbaEditableInformation')}</CardTitle>
                  <FieldGrid>
                    <Form.Item
                      label={t('damageArea')}
                      name="areaCode"
                      rules={[{ required: true }]}
                    >
                      <Select
                        showSearch
                        disabled={!canEditDamage}
                        placeholder={DEFAULT_OPTION}
                        options={areaCodeOptions}
                        onChange={() => {
                          form.setFieldValue('partCode', '');
                          form.setFieldValue('damageConditionCode', '');
                          form.setFieldValue('severityId', '');
                          form.setFieldValue('repairMethodCode', '');
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t('damageCause')}
                      name="damageCauseCode"
                      rules={[{ required: true }]}
                    >
                      <Select
                        showSearch
                        disabled={!canEditDamage}
                        placeholder={DEFAULT_OPTION}
                        loading={isDamageCodesLoading}
                        options={allDamageCodes?.damageCauses}
                      />
                    </Form.Item>
                    <Form.Item label={t('sector')} name="sectorIndices">
                      <Select
                        showSearch
                        disabled={!canEditDamage}
                        placeholder={DEFAULT_OPTION}
                        options={sectorIndicesOptions.map((option) => ({
                          label: option,
                          value: option,
                        }))}
                        mode="multiple"
                      />
                    </Form.Item>
                    <Form.Item
                      label={t('internalGrade')}
                      name="internalGradingId"
                      rules={[{ required: true }]}
                    >
                      <Select
                        showSearch
                        disabled={!canEditDamage}
                        placeholder={DEFAULT_OPTION}
                        loading={isDamageCodesLoading}
                        options={allDamageCodes?.internalGradings}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t('workCentre')}
                      name="workCenterCode"
                      rules={[{ required: true }]}
                    >
                      <Select
                        showSearch
                        disabled={!workCentreEditable}
                        placeholder={DEFAULT_OPTION}
                        loading={isDamageCodesLoading}
                        options={allDamageCodes?.workCenters}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t('responsibility')}
                      name="responsibilityCode"
                      rules={[{ required: true }]}
                    >
                      <Select
                        showSearch
                        disabled={!canEditDamage}
                        placeholder={DEFAULT_OPTION}
                        loading={isDamageCodesLoading}
                        options={allDamageCodes?.responsibilities.options}
                        onChange={() => {
                          form.setFieldValue('responsibilityShortcutCode', '');
                          form.setFieldValue('dealerCode', '');
                        }}
                      />
                    </Form.Item>
                    {responsibilityShortcutsOptions?.length ? (
                      <Form.Item
                        label={t('shortcutCode')}
                        name="responsibilityShortcutCode"
                        rules={[{ required: true }]}
                      >
                        <Select
                          showSearch
                          disabled={!canEditDamage}
                          placeholder={DEFAULT_OPTION}
                          loading={isDamageCodesLoading}
                          options={responsibilityShortcutsOptions}
                        />
                      </Form.Item>
                    ) : (
                      <Form.Item name="responsibilityShortcutCode" hidden>
                        <Input />
                      </Form.Item>
                    )}
                    {availableDealersOptions?.length ? (
                      <Form.Item
                        label={t('dealer')}
                        name="dealerCode"
                        rules={[{ required: true }]}
                      >
                        <Select
                          showSearch
                          disabled={!canEditDamage}
                          placeholder={DEFAULT_OPTION}
                          loading={isDamageCodesLoading}
                          options={availableDealersOptions}
                        />
                      </Form.Item>
                    ) : (
                      <Form.Item name="dealerCode" hidden>
                        <Input />
                      </Form.Item>
                    )}
                    <Form.Item
                      className="full-width-field"
                      label={t('notes')}
                      name="note"
                      extra={t('shortcutSaveNotes', {
                        shortcut: saveNotesShortcutHint(),
                      })}
                    >
                      <Input.TextArea
                        rows={3}
                        disabled={!canEditDamage}
                        onKeyDown={(event) => {
                          if (!isSaveNotesShortcut(event.nativeEvent)) return;
                          event.preventDefault();
                          form.submit();
                        }}
                      />
                    </Form.Item>
                    {mechanicalInspectionData?.isMechanical ? (
                      <Form.Item
                        label={t('mechanicalFailure')}
                        name="mechanicalFailure"
                        rules={
                          isDealerCodeRequired
                            ? [{ required: true }]
                            : undefined
                        }
                      >
                        <Select
                          showSearch
                          disabled={!canEditDamage}
                          placeholder={DEFAULT_OPTION}
                          loading={isMechanicalInspectionDataLoading}
                          options={mechanicalInspectionData?.failToReportList}
                        />
                      </Form.Item>
                    ) : (
                      <Form.Item name="mechanicalFailure" hidden>
                        <Input />
                      </Form.Item>
                    )}
                  </FieldGrid>
                </EditMain>
                <EditAside>
                  <ReadOnlyItem>
                    <ReadOnlyLabel>
                      <LockOutlined />
                      {t('labourTime')}
                    </ReadOnlyLabel>
                    <ReadOnlyValue title={labourTime || emptyValue}>
                      {labourTime || emptyValue}
                    </ReadOnlyValue>
                  </ReadOnlyItem>
                  <ReadOnlyItem>
                    <ReadOnlyLabel>
                      <LockOutlined />
                      {t('paintTime')}
                    </ReadOnlyLabel>
                    <ReadOnlyValue title={emptyValue}>
                      {emptyValue}
                    </ReadOnlyValue>
                  </ReadOnlyItem>
                  <ReadOnlyItem>
                    <ReadOnlyLabel>
                      <LockOutlined />
                      {t('totalTime')}
                    </ReadOnlyLabel>
                    <ReadOnlyValue title={emptyValue}>
                      {emptyValue}
                    </ReadOnlyValue>
                  </ReadOnlyItem>
                  <ReadOnlyItem>
                    <ReadOnlyLabel>
                      <LockOutlined />
                      {t('frCodes')}
                    </ReadOnlyLabel>
                    {frCodeList.length ? (
                      <TagList>
                        {frCodeList.map((code, index) => (
                          <CodeTag key={`${code}-${index}`} title={code}>
                            {code}
                          </CodeTag>
                        ))}
                      </TagList>
                    ) : (
                      <ReadOnlyValue>{emptyValue}</ReadOnlyValue>
                    )}
                  </ReadOnlyItem>
                  <ReadOnlyItem>
                    <ReadOnlyLabel>
                      <LockOutlined />
                      {t('parts')}
                    </ReadOnlyLabel>
                    {partNumberList.length ? (
                      <TagList>
                        {partNumberList.map((partNumber, index) => (
                          <CodeTag
                            key={`${partNumber}-${index}`}
                            title={partNumber}
                          >
                            {partNumber}
                          </CodeTag>
                        ))}
                      </TagList>
                    ) : (
                      <ReadOnlyValue>{emptyValue}</ReadOnlyValue>
                    )}
                  </ReadOnlyItem>
                </EditAside>
              </EditColumns>
            </Card>
          </Layout>
        </Form>
      </Spin>
    );
  }

  return (
    <Spin spinning={loading}>
      <Form
        id={formId}
        form={form}
        onFinish={handleSubmit}
        onValuesChange={() => onDirtyChange?.(true)}
        onKeyDown={preventEnterSaveWhileDropdownOpen}
        layout="vertical"
        size="middle"
      >
        <FieldGrid>
          <Form.Item
            label={t('damageArea')}
            name="areaCode"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              placeholder={DEFAULT_OPTION}
              options={areaCodeOptions || []}
              onChange={() => {
                form.setFieldValue('partCode', '');
                form.setFieldValue('damageConditionCode', '');
                form.setFieldValue('severityId', '');
                form.setFieldValue('repairMethodCode', '');
              }}
            />
          </Form.Item>

          <Form.Item
            label={t('partComp')}
            name="partCode"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              loading={isDamagePartsLoading}
              placeholder={DEFAULT_OPTION}
              options={areaCode ? partCodeOptionsMap[areaCode] : []}
              onChange={() => {
                form.setFieldValue('damageConditionCode', '');
                form.setFieldValue('severityId', '');
                form.setFieldValue('repairMethodCode', '');
              }}
            />
          </Form.Item>

          <Form.Item
            label={t('damageCondition')}
            name="damageConditionCode"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              loading={isDamageCodesLoading || isDamageConditionCodesLoading}
              placeholder={DEFAULT_OPTION}
              options={damageConditionCodes?.map((item) => ({
                label: item.description,
                value: item.code,
              }))}
              onChange={() => {
                form.setFieldValue('severityId', '');
                form.setFieldValue('repairMethodCode', '');
              }}
            />
          </Form.Item>

          {damageSeverityCodes?.length ? (
            <Form.Item
              label={t('severity')}
              name="severityId"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                loading={
                  isDamageCodesLoading ||
                  isDamageConditionCodesLoading ||
                  isDamageSeverityCodesLoading
                }
                placeholder={DEFAULT_OPTION}
                options={damageSeverityCodes?.map((item) => ({
                  label: item.description,
                  value: item.severityId,
                }))}
                onChange={() => form.setFieldValue('repairMethodCode', '')}
              />
            </Form.Item>
          ) : null}

          <Form.Item
            label={t('repairMethod')}
            name="repairMethodCode"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              loading={
                isDamageCodesLoading ||
                isDamageConditionCodesLoading ||
                isDamageSeverityCodesLoading ||
                isDamageRepairMethodsLoading
              }
              placeholder={DEFAULT_OPTION}
              options={damageRepairMethods?.map((item) => ({
                label: item.description,
                value: item.code,
              }))}
            />
          </Form.Item>

          <Form.Item
            label={t('damageCause')}
            name="damageCauseCode"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              loading={isDamageCodesLoading}
              placeholder={DEFAULT_OPTION}
              options={allDamageCodes?.damageCauses}
            />
          </Form.Item>

          <Form.Item label={t('sector')} name="sectorIndices">
            <Select
              showSearch
              placeholder={DEFAULT_OPTION}
              options={sectorIndicesOptions.map((o) => ({
                label: o,
                value: o,
              }))}
              mode="multiple"
            />
          </Form.Item>

          <Form.Item
            label={t('internalGrade')}
            name="internalGradingId"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              loading={isDamageCodesLoading}
              placeholder={DEFAULT_OPTION}
              options={allDamageCodes?.internalGradings}
            />
          </Form.Item>

          <Form.Item
            label={t('workCentre')}
            name="workCenterCode"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              loading={isDamageCodesLoading}
              placeholder={DEFAULT_OPTION}
              options={allDamageCodes?.workCenters}
            />
          </Form.Item>

          <Form.Item
            label={t('responsibility')}
            name="responsibilityCode"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              loading={isDamageCodesLoading}
              placeholder={DEFAULT_OPTION}
              options={allDamageCodes?.responsibilities.options}
              onChange={() => {
                form.setFieldValue('responsibilityShortcutCode', '');
                form.setFieldValue('dealerCode', '');
              }}
            />
          </Form.Item>

          {responsibilityShortcutsOptions?.length ? (
            <Form.Item
              label={t('shortcutCode')}
              name="responsibilityShortcutCode"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                loading={isDamageCodesLoading}
                placeholder={DEFAULT_OPTION}
                options={responsibilityShortcutsOptions}
              />
            </Form.Item>
          ) : null}

          {availableDealersOptions?.length ? (
            <Form.Item
              label={t('dealer')}
              name="dealerCode"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                loading={isDamageCodesLoading}
                placeholder={DEFAULT_OPTION}
                options={availableDealersOptions}
              />
            </Form.Item>
          ) : null}

          {mechanicalInspectionData?.isMechanical ? (
            <Form.Item
              label={t('mechanicalFailure')}
              name="mechanicalFailure"
              rules={isDealerCodeRequired ? [{ required: true }] : undefined}
            >
              <Select
                showSearch
                loading={isMechanicalInspectionDataLoading}
                placeholder={DEFAULT_OPTION}
                options={mechanicalInspectionData?.failToReportList}
              />
            </Form.Item>
          ) : null}

          <FieldPair>
            <Form.Item
              label={t('notes')}
              name="note"
              extra={t('shortcutSaveNotes', {
                shortcut: saveNotesShortcutHint(),
              })}
            >
              <Input.TextArea
                rows={3}
                onKeyDown={(event) => {
                  if (!isSaveNotesShortcut(event.nativeEvent)) return;
                  event.preventDefault();
                  form.submit();
                }}
              />
            </Form.Item>
            {showDamageImageFiles ? (
              <Form.Item label={t('damageImages')}>
                <DragUpload
                  accept={acceptImageFiles}
                  uploadButtonText={t('upload')}
                  maxFileSize={MAX_FILE_SIZE}
                  showUploadBtn={false}
                  onFileChanged={handleValueChange}
                  multiple
                />
              </Form.Item>
            ) : null}
          </FieldPair>
        </FieldGrid>
      </Form>
    </Spin>
  );
};

type DamageLinePopupProps = {
  open: boolean;
  mode: 'add' | 'edit';
  inspection: Inspection;
  damageLine?: Damage;
  /** Saved damage line, without inline or staged edits. */
  originalDamageLine?: Damage;
  onRestore?: (damageLineId: number) => void;
  onSubmit: () => void;
  /** Edit mode keeps the line in the page cache instead of saving immediately. */
  onStage?: (edit: StagedDamageEdit) => void;
  /** True when this damage line already has unapplied staged changes. */
  staged?: boolean;
  onClose: () => void;
  afterClose?: () => void;
};

export const DamageLinePopup: FC<DamageLinePopupProps> = ({
  open,
  mode,
  inspection,
  damageLine,
  originalDamageLine,
  onRestore,
  onSubmit,
  onStage,
  staged = false,
  onClose,
  afterClose,
}) => {
  const { t } = useTranslate();
  const { modal } = App.useApp();
  const { hasGbaAccess } = useAppCustomers();
  const isEdit = mode === 'edit';
  const formId = isEdit ? 'damage-line-form-edit' : 'damage-line-form-add';
  const undoRef = useRef<() => void>(() => undefined);
  const { mutateAsync: createDamagesLinesAsync, isPending: isCreating } =
    useCreateInspectionsVehiclesDamagesLines();
  const isPending = isCreating;
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (open) setIsDirty(false);
  }, [open]);

  const requestClose = useCallback(() => {
    if (isPending) return;
    if (!isDirty) {
      onClose();
      return;
    }
    confirmDiscardUnsaved(
      modal,
      {
        title: t('discardUnsavedChanges'),
        content: t('discardUnsavedChangesBody'),
        okText: t('yes'),
        cancelText: t('no'),
      },
      onClose,
    );
  }, [isDirty, isPending, modal, onClose, t]);

  const createDamage = useCallback(
    async (data: DamageLineFormType) => {
      const body: CreateDamageLineBody = {
        ...data,
        sectorIndices: data.sectorIndices.filter(Boolean),
        inspectionId: inspection.id,
        siteId: inspection.locationId,
      };
      await createDamagesLinesAsync(body);
      onSubmit();
      onClose();
    },
    [
      createDamagesLinesAsync,
      inspection.id,
      inspection.locationId,
      onClose,
      onSubmit,
    ],
  );

  const handleSubmit = useCallback(
    async (
      data: DamageLineFormType,
      staged?: Pick<
        StagedDamageEdit,
        'display' | 'salesGradeValue' | 'valueForGrading'
      >,
    ) => {
      if (isEdit) {
        if (!damageLine) return;

        const body: UpdateDamageLineBody = {
          areaCode: data.areaCode,
          partCode: data.partCode,
          damageCauseCode: data.damageCauseCode,
          sectorIndices: data.sectorIndices.filter(Boolean),
          damageConditionCode: data.damageConditionCode,
          severityId: data.severityId,
          repairMethodCode: data.repairMethodCode,
          internalGradingId: data.internalGradingId,
          workCenterCode: data.workCenterCode,
          responsibilityCode: data.responsibilityCode,
          responsibilityShortcutCode: data.responsibilityShortcutCode,
          dealerCode: data.dealerCode,
          note: data.note,
          mechanicalFailure: data.mechanicalFailure,
          damageLineId: damageLine.id,
          inspectionId: inspection.id,
          siteId: inspection.locationId,
        };

        onStage?.({
          body,
          display: staged?.display ?? {},
          salesGradeValue: staged?.salesGradeValue,
          valueForGrading: staged?.valueForGrading,
        });
        onClose();
        return;
      }

      if (!data.damageImageFiles?.length) {
        modal.confirm({
          title: 'No image uploaded',
          content: 'Are you sure to proceed?',
          okText: t('yes'),
          onOk: () => createDamage(data),
        });
        return;
      }

      await createDamage(data);
    },
    [
      createDamage,
      damageLine,
      inspection.id,
      inspection.locationId,
      isEdit,
      modal,
      onClose,
      onStage,
      t,
    ],
  );

  return (
    <Modal
      open={open}
      onCancel={requestClose}
      afterClose={afterClose}
      width={isEdit ? 1120 : 800}
      destroyOnHidden
      keyboard={!isPending}
      maskClosable={!isPending}
      title={
        isEdit ? (
          <div>
            <EditTitleRow>
              <span>{t('editDamage')}</span>
              {staged ? (
                <StagingModeBadge data-testid="damage-staging-mode">
                  <StagingModeDot />
                  {t('damageStagingModeHint')}
                </StagingModeBadge>
              ) : null}
            </EditTitleRow>
            <EditTitleHint>
              {hasGbaAccess
                ? t('gbaRepairControl')
                : t('customerAuthorisation', {
                    customerName: inspection.customerName,
                  })}
            </EditTitleHint>
          </div>
        ) : (
          t('addDamage')
        )
      }
      styles={{
        body: {
          maxHeight: 'calc(100vh - 220px)',
          overflowY: 'auto',
        },
      }}
      footer={
        <Footer>
          {isEdit ? (
            <Button
              icon={<span>↺</span>}
              aria-label={t('restoreOriginal')}
              onClick={() => {
                undoRef.current();
                if (damageLine) onRestore?.(damageLine.id);
              }}
            >
              {t('restoreOriginal')}
            </Button>
          ) : null}
          <FooterActions>
            <Button className="footer-cancel" onClick={requestClose}>
              {t('cancel')}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              form={formId}
              loading={isPending}
            >
              {isEdit ? t('stageChangesToTable') : t('createDamage')}
            </Button>
          </FooterActions>
        </Footer>
      }
    >
      <DamageLineForm
        formId={formId}
        inspection={inspection}
        damageLine={isEdit ? damageLine : undefined}
        originalDamageLine={isEdit ? originalDamageLine : undefined}
        onSubmit={handleSubmit}
        presentation={isEdit ? 'authorisation' : 'default'}
        loading={isPending}
        undoRef={undoRef}
        onDirtyChange={setIsDirty}
      />
    </Modal>
  );
};
